
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ArtworkModal from '../gallery/components/ArtworkModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Artwork {
  id: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at: string;
}

export default function FavoritesPage() {
  const [favorites, setFavorites] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState(false);
  const [cartItems, setCartItems] = useState<number[]>([]);

  useEffect(() => {
    checkUser();

    // SEO Schema.org JSON-LD for Favorites
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Favori Eserlerim - Nox Sanat",
      "description": "Beğendiğiniz ve kalbiminize dokunan sanat eserlerinizin özel koleksiyonu",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/favorites`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "Favori Sanat Eserleri",
        "description": "Kullanıcının beğendiği sanat eserleri koleksiyonu"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Ana Sayfa",
            "item": import.meta.env.VITE_SITE_URL || "https://example.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Favoriler",
            "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/favorites`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);
    
    if (!session?.user) {
      setIsLoading(false);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        fetchFavorites();
      } else {
        setFavorites([]);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  };

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          artwork_id,
          artworks (
            id,
            title,
            artist,
            technique,
            category,
            size,
            price,
            year,
            materials,
            description,
            image_url,
            created_at
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const favoriteArtworks = data?.map(item => item.artworks).filter(Boolean) || [];
      setFavorites(favoriteArtworks as Artwork[]);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFavorite = async (artworkId: number) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('artwork_id', artworkId);

      if (error) throw error;

      setFavorites(prev => prev.filter(artwork => artwork.id !== artworkId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const addToCart = (id: number) => {
    setCartItems(prev => [...prev, id]);
    const button = document.querySelector(`[data-cart-btn="${id}"]`);
    if (button) {
      button.textContent = 'Eklendi!';
      setTimeout(() => {
        button.textContent = 'Sepete Ekle';
      }, 1500);
    }
  };

  const openArtworkModal = (artwork: Artwork) => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
  };

  const navigateToHome = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const navigateToGallery = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/gallery');
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">Favoriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header 
          cartCount={cartItems.length}
          onHomeClick={navigateToHome}
          onAboutClick={() => scrollToSection('about-section')}
          onContactClick={() => scrollToSection('contact')}
        />
        
        <div className="pt-32 pb-16">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-6 bg-gray-800 rounded-full">
                <i className="ri-heart-line text-4xl text-gray-400"></i>
              </div>
              <h1 className="text-3xl font-bold mb-4">Favorilerinizi Görüntüleyin</h1>
              <p className="text-gray-400 mb-8">
                Beğendiğiniz eserleri favorilere eklemek ve görüntülemek için giriş yapmanız gerekiyor.
              </p>
              <button 
                onClick={navigateToHome}
                className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Giriş Yap
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        cartCount={cartItems.length}
        onHomeClick={navigateToHome}
        onAboutClick={() => scrollToSection('about-section')}
        onContactClick={() => scrollToSection('contact')}
      />
      
      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-16 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('https://public.readdy.ai/ai/img_res/203e6977fdb7940af8d4ec358d7d3fc8.jpg')`
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Favori Eserlerim</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Beğendiğiniz ve kalbiminize dokunan sanat eserlerinizin özel koleksiyonu
          </p>
        </div>
      </section>

      {/* Favorites Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-6">
          {favorites.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-gray-800 rounded-full">
                <i className="ri-heart-line text-5xl text-gray-400"></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">Henüz favori eseriniz yok</h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Galerimizdeki eserleri keşfedin ve beğendiğiniz eserleri kalp butonuna tıklayarak favorilerinize ekleyin.
              </p>
              <button 
                onClick={navigateToGallery}
                className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Galeriyi Keşfet
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">
                  {favorites.length} Favori Eser
                </h2>
                <p className="text-gray-400">
                  Beğendiğiniz eserlerin özel koleksiyonu
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-product-shop>
                {favorites.map((artwork) => (
                  <div 
                    key={artwork.id} 
                    className="group bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => openArtworkModal(artwork)}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden">
                      <img 
                        src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder%2C%20artistic%20background%20with%20paint%20textures%2C%20creative%20artwork%20display%2C%20gallery%20quality%20art%20piece&width=400&height=500&seq=favplaceholder&orientation=portrait"}
                        alt={artwork.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 right-4 flex gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFavorite(artwork.id);
                          }}
                          className="w-10 h-10 flex items-center justify-center bg-red-500/80 backdrop-blur-sm rounded-full text-white hover:bg-red-600/80 transition-colors cursor-pointer"
                          title="Favorilerden Kaldır"
                        >
                          <i className="ri-heart-fill text-lg"></i>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            openArtworkModal(artwork);
                          }}
                          className="w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-full text-gray-300 hover:text-white transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-white/90 text-black text-xs px-3 py-1 rounded-full font-medium">
                          {artwork.technique}
                        </span>
                      </div>
                      {/* Multiple Images Indicator */}
                      {artwork.images && artwork.images.length > 1 && (
                        <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <i className="ri-image-line"></i>
                          {artwork.images.length}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-lg font-bold mb-2 group-hover:text-gray-300 transition-colors">
                        {artwork.title}
                      </h3>
                      <p className="text-gray-400 mb-1 text-sm">Sanatçı: {artwork.artist}</p>
                      <p className="text-gray-500 text-xs mb-4">Boyut: {artwork.size}</p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-white">{artwork.price}</span>
                        <button 
                          data-cart-btn={artwork.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(artwork.id);
                          }}
                          className="bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                        >
                          Sepete Ekle
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-12">
                <button 
                  onClick={navigateToGallery}
                  className="bg-gray-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Daha Fazla Eser Keşfet
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Artwork Detail Modal */}
      <ArtworkModal
        artwork={selectedArtwork}
        isOpen={showArtworkModal}
        onClose={() => setShowArtworkModal(false)}
        onAddToCart={addToCart}
        onToggleLike={() => selectedArtwork && removeFavorite(selectedArtwork.id)}
        isLiked={true}
      />

      <Footer />
    </div>
  );
}
